'use strict'

import CMYKColor from './CMYKColor.js'
import ColorsMath from './ColorsMath.js'
import LABColor from './LABColor.js'
import RGBColor from './RGBColor.js'

/**
 * CXICCTransform
 * Workflow:
 * - Expose ICC-transform shaped APIs.
 * - Provide sRGB/LAB fallbacks until native ICC transforms are wired.
 */
class CXICCTransform {
    /**
     * Transform an image buffer between profiles.
     * @param {Uint8Array} inImage
     * @param {Uint8Array} outImage
     * @param {Object} inRGBProfile
     * @param {Object} outRGBProfile
     */
    static transformImage(inImage, outImage, inRGBProfile, outRGBProfile) {
        CXICCTransform._warnUnsupported('transformImage')
        throw new Error('[CXICCTransform] ICC image transforms are not implemented on web.')
    }

    /**
     * Transform RGB to RGB using ICC profiles (fallback is identity).
     * @param {RGBColor} inRGBColor
     * @param {boolean} clipped
     * @param {Object} inRGBProfile
     * @param {Object} outRGBProfile
     * @returns {RGBColor}
     */
    static transformRGBTORGB(inRGBColor, clipped, inRGBProfile, outRGBProfile) {
        CXICCTransform._warnUnsupported('transformRGBTORGB')
        const rgb = new RGBColor(inRGBColor.r, inRGBColor.g, inRGBColor.b)
        return clipped ? CXICCTransform._clipRGB(rgb) : rgb
    }

    /**
     * Transform RGB to LAB using ICC profiles (fallback uses sRGB D65).
     * @param {RGBColor} inRGBColor
     * @param {boolean} clipped
     * @param {Object} inRGBProfile
     * @param {Object} outLabProfile
     * @returns {LABColor}
     */
    static transformRGBToLab(inRGBColor, clipped, inRGBProfile, outLabProfile) {
        CXICCTransform._warnUnsupported('transformRGBToLab')
        const lab = ColorsMath.cxSRGBToLAB_2D65(inRGBColor)
        if (!clipped) return lab
        return new LABColor(
            LABColor.inLRange(lab.l),
            LABColor.inABRange(lab.a),
            LABColor.inABRange(lab.b),
        )
    }

    /**
     * Transform LAB to RGB using ICC profiles (fallback uses sRGB D65).
     * @param {LABColor} inLabColor
     * @param {boolean} clipped
     * @param {Object} inLabProfile
     * @param {Object} outRGBProfile
     * @returns {RGBColor}
     */
    static transformLabToRGB(inLabColor, clipped, inLabProfile, outRGBProfile) {
        CXICCTransform._warnUnsupported('transformLabToRGB')
        const rgb = ColorsMath.cxLABToSRGB_2D65(inLabColor)
        return clipped ? CXICCTransform._clipRGB(rgb) : rgb
    }

    /**
     * Transform RGB to CMYK using ICC profiles (fallback is naive conversion).
     * @param {RGBColor} inRGBColor
     * @param {Object} inRGBProfile
     * @param {Object} outCMYKProfile
     * @returns {CMYKColor}
     */
    static transformRGBTOCMYK(inRGBColor, inRGBProfile, outCMYKProfile) {
        CXICCTransform._warnUnsupported('transformRGBTOCMYK')
        return CXICCTransform._rgbToCmyk(inRGBColor)
    }

    /**
     * Clamp RGB to 0..255.
     * @param {RGBColor} rgb
     * @returns {RGBColor}
     */
    static _clipRGB(rgb) {
        return new RGBColor(
            RGBColor.inRGBRange(Math.round(rgb.r)),
            RGBColor.inRGBRange(Math.round(rgb.g)),
            RGBColor.inRGBRange(Math.round(rgb.b)),
        )
    }

    /**
     * Naive RGB -> CMYK conversion (used as a placeholder).
     * @param {RGBColor} rgb
     * @returns {CMYKColor}
     */
    static _rgbToCmyk(rgb) {
        const r = rgb.r / 255.0
        const g = rgb.g / 255.0
        const b = rgb.b / 255.0

        const k = 1 - Math.max(r, g, b)
        if (k >= 1) {
            return new CMYKColor(0, 0, 0, 100)
        }

        const c = (1 - r - k) / (1 - k)
        const m = (1 - g - k) / (1 - k)
        const y = (1 - b - k) / (1 - k)

        return new CMYKColor(c * 100, m * 100, y * 100, k * 100)
    }

    /**
     * Log a warning once per operation until ICC transforms are implemented.
     * @param {string} operation
     */
    static _warnUnsupported(operation) {
        if (!CXICCTransform._warnings) {
            CXICCTransform._warnings = new Set()
        }
        if (CXICCTransform._warnings.has(operation)) return
        CXICCTransform._warnings.add(operation)
        console.warn(`[CXICCTransform] ${operation} uses sRGB fallback on web.`)
    }
}

// Enum-like list of ICC transform types (kept for API parity).
CXICCTransform.ICCTransformType = Object.freeze({
    RGB2RGB: Object.freeze({ value: 0, getValue() { return this.value } }),
    RGB2Lab: Object.freeze({ value: 1, getValue() { return this.value } }),
    Lab2RGB: Object.freeze({ value: 2, getValue() { return this.value } }),
    RGB2CMYK: Object.freeze({ value: 3, getValue() { return this.value } }),
    ImageRGB2ImageRGB: Object.freeze({ value: 4, getValue() { return this.value } }),
})

export default CXICCTransform
